import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Field, TextArea, EmptyState, Modal, Alert, Spinner } from "@/components/ui";
import { Plus, Heart, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Patients() {
  const [items, setItems] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => { const r = await api.listPatients(); setItems(r.items || []); }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name} from your care circle?`)) return;
    try { await api.deletePatient(p.id); await load(); toast.success("Care recipient removed"); }
    catch (e) { toast.error(e?.detail || "Could not remove"); }
  };
  const makePrimary = async (p) => { await api.updatePatient(p.id, { is_primary: true }); await load(); toast.success(`${p.name} is now primary`); };

  if (!items) return <Spinner />;

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="Care circle" title="Care recipients"
        action={<Button variant="accent" data-testid="add-patient-button" onClick={() => setAddOpen(true)}><Plus className="w-4 h-4" /> Add</Button>} />

      {items.length === 0 ? (
        <Card><EmptyState testid="patients-empty" icon={Heart} title="No care recipients yet"
          body="Add the loved ones you're caring for to manage their meds, vitals, and claims."
          action={<Button data-testid="patients-empty-add" onClick={() => setAddOpen(true)}>Add a care recipient</Button>} /></Card>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <Card key={p.id} data-testid={`patient-${p.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-brand flex items-center justify-center text-white font-semibold shrink-0">{p.name?.[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-ink truncate">{p.name}</span>
                    {p.is_primary && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-brand-dark bg-mint-tint border border-mint-dark rounded-full px-1.5 py-0.5"><Star className="w-3 h-3" />Primary</span>}
                  </div>
                  <div className="text-sm text-subtle">{[p.dob && `DOB ${p.dob}`, p.conditions].filter(Boolean).join(" · ") || "No details yet"}</div>
                </div>
                {!p.is_primary && <button data-testid={`make-primary-${p.id}`} onClick={() => makePrimary(p)} className="text-xs font-semibold text-brand hover:underline">Set primary</button>}
                <button data-testid={`delete-patient-${p.id}`} onClick={() => remove(p)} className="p-1.5 text-subtle hover:text-danger"><Trash2 className="w-4 h-4" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AddPatientModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={async () => { setAddOpen(false); await load(); toast.success("Care recipient added"); }} />
    </div>
  );
}

function AddPatientModal({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [conditions, setConditions] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null); setBusy(true);
    try {
      await api.createPatient({ name: name.trim(), dob: dob.trim() || null, conditions: conditions.trim() || null, phone: phone.trim() || null });
      setName(""); setDob(""); setConditions(""); setPhone("");
      onSaved();
    } catch (e) { setErr(e?.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add care recipient" testid="add-patient-modal">
      <Field label="Name" data-testid="patient-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Elena Rivera" />
      <Field label="Date of birth (optional)" data-testid="patient-dob-input" value={dob} onChange={(e) => setDob(e.target.value)} placeholder="1948-03-15" />
      <Field label="Phone (optional)" data-testid="patient-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" />
      <TextArea label="Conditions / notes (optional)" data-testid="patient-conditions-input" value={conditions} onChange={(e) => setConditions(e.target.value)} rows={3} placeholder="Type 2 diabetes, mild hypertension" />
      <Alert testid="add-patient-error">{err}</Alert>
      <Button data-testid="save-patient-button" onClick={save} loading={busy} disabled={!name.trim()} className="w-full">Save care recipient</Button>
    </Modal>
  );
}
